import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NAV } from './NavConfig';
import { useAuth } from '@/app/providers/AuthProvider';
import { useToast } from '@/app/providers/ToastProvider';
import { useTenantScope } from '@/app/providers/TenantScopeProvider';
import { buildScopedPath } from '@/app/routes';
import { featureLabel, normalizePlan, planHasFeature } from '@/shared/format/plan';

interface PaletteItem {
  routeId: string;
  routeKey: import('@/app/routes').ScopedRouteKey;
  icon: string;
  label: string;
  groupLabel: string;
  requiredFeature?: import('@/shared/format/plan').FeatureCode | undefined;
}

interface Props {
  onUpgrade: () => void;
}

export function CommandPalette({ onUpgrade }: Props) {
  const navigate = useNavigate();
  const scope = useTenantScope();
  const { snapshot } = useAuth();
  const { toast } = useToast();
  const planId = normalizePlan(snapshot.plan);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const allItems = useMemo<PaletteItem[]>(
    () =>
      NAV.flatMap((g) =>
        g.links.map((l) => ({
          routeId: l.routeId,
          routeKey: l.routeKey,
          icon: l.icon,
          label: l.label,
          groupLabel: g.label,
          requiredFeature: l.requiredFeature,
        })),
      ),
    [],
  );

  // Ctrl/Cmd+K — нээх/хаах.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSel(0);
      // focus input дараагийн frame-д.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? allItems.filter(
            (i) =>
              i.label.toLowerCase().includes(q) || i.groupLabel.toLowerCase().includes(q),
          )
        : allItems,
    [q, allItems],
  );

  useEffect(() => {
    setSel((s) => Math.min(s, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  if (!open) return null;

  const go = (item: PaletteItem) => {
    if (item.requiredFeature && !planHasFeature(planId, item.requiredFeature)) {
      toast(
        `'${featureLabel(item.requiredFeature)}' функц одоогийн багцад идэвхгүй. Багц шинэчилнэ үү.`,
        'warning',
      );
      onUpgrade();
      setOpen(false);
      return;
    }
    navigate(buildScopedPath(item.routeKey, scope));
    setOpen(false);
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[sel];
      if (item) go(item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="ts-cmdk-overlay" onMouseDown={() => setOpen(false)}>
      <div className="ts-cmdk" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ts-cmdk-search">
          <i className="bi bi-search" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Хайх... (цэс, хуудас)"
            aria-label="Команд хайх"
          />
          <kbd className="ts-cmdk-kbd">ESC</kbd>
        </div>
        <div className="ts-cmdk-list">
          {filtered.length === 0 && <div className="ts-cmdk-empty">Илэрц олдсонгүй</div>}
          {filtered.map((item, idx) => {
            const locked = item.requiredFeature && !planHasFeature(planId, item.requiredFeature);
            return (
              <button
                type="button"
                key={item.routeId}
                className={`ts-cmdk-item${idx === sel ? ' active' : ''}`}
                onMouseEnter={() => setSel(idx)}
                onClick={() => go(item)}
              >
                <span className="ts-cmdk-icon">
                  <i className={`bi bi-${item.icon}`} />
                </span>
                <span className="ts-cmdk-label">{item.label}</span>
                <span className="ts-cmdk-group">{item.groupLabel}</span>
                {locked && (
                  <span className="ts-cmdk-lock">
                    <i className="bi bi-lock-fill" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
